import React from 'react';
import {
  Flex,
  Box,
  Table,
  Tbody,
  Text,
  Thead,
  Tr,
  useColorModeValue,
  IconButton,
} from '@chakra-ui/react';
import {
  createColumnHelper,
} from '@tanstack/react-table';
import PageTable from 'components/layout/PageTable';
import AdminTableCell from 'components/layout/AdminTableCell';
import SortableTableHeader from 'components/layout/SortableTableHeader';
import { useSortableAdminTable } from 'hooks/useSortableAdminTable';
import { getSortableColumnOptions } from 'utils/tableColumnOptions';
import TablePagination from 'components/layout/TablePagination';
import ServicesEditModal from './ServicesEditModal'; // Importa el nuevo modal
import DuplicateServiceModal from './DuplicateServiceModal';
import DeleteModal from '../../components/DeleteModal/DeleteModal';
import { formatNumber } from '../../utils/transform';

const columnHelper = createColumnHelper();

export default function ServicesTable({
  columnsData,
  data,
  page,
  setPage,
  totalPages,
  pageSize,
  setPageSize,
  handleUpdateService,
  handleDuplicateService,
  sorting,
  onSortingChange,
}) {
  const [selectedService, setSelectedService] = React.useState(null);
  const [modalState, setModalState] = React.useState({ isOpen: false });

  const [duplicateModalState, setDuplicateModalState] = React.useState({
    isOpen: false,
  });
  const [serviceToDuplicate, setServiceToDuplicate] = React.useState(null);

  const [serviceToDelete, setServiceToDelete] = React.useState(null);
  const [deleteModalState, setDeleteModalState] = React.useState({
    isOpen: false,
  });

  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');
  const columns = React.useMemo(
    () => createColumns(columnsData),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [columnsData],
  );

  const table = useSortableAdminTable({
    data,
    columns,
    sorting,
    onSortingChange,
  });

  const openEditModal = (rowData) => {
    setSelectedService(rowData);
    setModalState({ isOpen: true });
  };

  const closeEditModal = () => {
    setModalState({ isOpen: false });
    setSelectedService(null);
  };

  const openDuplicateModal = (rowData) => {
    setServiceToDuplicate(rowData);
    setDuplicateModalState({ isOpen: true });
  };

  const closeDuplicateModal = () => {
    setDuplicateModalState({ isOpen: false });
    setServiceToDuplicate(null);
  };

  const handleDuplicate = () => {
    handleDuplicateService(serviceToDuplicate); // Llamada a la función para duplicar el servicio
    closeDuplicateModal();
  };

  const openDeleteModal = (rowData) => {
    setServiceToDelete(rowData);
    setDeleteModalState({ isOpen: true });
  };

  const closeDeleteModal = () => {
    setDeleteModalState({ isOpen: false });
    setServiceToDelete(null);
  };

  const handleDelete = () => {
    const updatedService = { ...serviceToDelete, enabled: !serviceToDelete.enabled };
    handleUpdateService(updatedService);
    closeDeleteModal();
  };

  function createColumns(columnsData) {
    return columnsData.map((col) =>
      columnHelper.accessor(col.accessor, {
        ...getSortableColumnOptions(col),
        header: () => (
          <Text fontSize={{ sm: '10px', lg: '12px' }}>{col.Header}</Text>
        ),
        cell: (info) => {
          const value = info.getValue();
          let displayValue = value;
          
          // Aplicar formateo de números para campos específicos
          if (col.accessor === 'volkswagenCost' || 
              col.accessor === 'servicesEvents.length' || 
              col.accessor === 'recommendedProducts.length') {
            displayValue = formatNumber(value);
          }

          if (col.accessor === 'serviceModels') {
            const row = info.row.original;
            const labels = (row.serviceModels || [])
              .map((sm) => sm.model?.description)
              .filter(Boolean);
            if (labels.length === 0) {
              displayValue = 'Todos';
            } else if (labels.length > 4) {
              displayValue = `${labels.length} modelos`;
            } else {
              displayValue = labels.join(', ');
            }
          }
          
          return (
            <Flex align="center">
              <Text fontSize="sm" fontWeight="normal">
                {displayValue}
              </Text>
              {col.actions &&
              col.actions.map((action, index) => (
                <IconButton
                  key={index}
                  aria-label={action.label}
                  icon={action.icon}
                  size="sm"
                  variant="ghost"
                  colorScheme={action.colorScheme}
                  ml={2}
                  onClick={() => {
                    if (action.type === 'edit') {
                      openEditModal(info.row.original);
                    } else if (action.type === 'copy') {
                      openDuplicateModal(info.row.original); // Abrir modal de duplicación
                    }
                    else if (action.type === 'delete') {
                      openDeleteModal(info.row.original);
                    }
                  }}
                />
              ))}
            </Flex>
          );
        },
      }),
    );
  }

  return (
    <PageTable
      flexDirection="column"
      w="100%"
      px="0px"
     >
      <Box>
        <PageTable.Scroll>
          <Table variant="simple" color="gray.700" mb="24px" mt="12px">
                    <Thead>
                      {table.getHeaderGroups().map((headerGroup) => (
                        <Tr key={headerGroup.id}>
                          {headerGroup.headers.map((header) => (
                            <SortableTableHeader
                              key={header.id}
                              header={header}
                              borderColor={borderColor}
                            />
                          ))}
                        </Tr>
                      ))}
                    </Thead>
                    <Tbody>
                      {table.getRowModel().rows.map((row) => (
                        <Tr key={row.id}>
                          {row.getVisibleCells().map((cell) => (
                                <AdminTableCell key={cell.id} cell={cell} />
                              ))}
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
        </PageTable.Scroll>
          <TablePagination
            page={page}
            setPage={setPage}
            totalPages={totalPages}
            pageSize={pageSize}
            setPageSize={setPageSize}
          />
      </Box>

      <ServicesEditModal
        isOpen={modalState.isOpen}
        onClose={closeEditModal}
        service={selectedService}
        onUpdate={handleUpdateService}
      />
      <DuplicateServiceModal
        isOpen={duplicateModalState.isOpen}
        onClose={closeDuplicateModal}
        onConfirm={handleDuplicate}
        serviceName={serviceToDuplicate?.name}
      />
      <DeleteModal
        isOpen={deleteModalState.isOpen}
        onClose={closeDeleteModal}
        onConfirm={handleDelete}
        name={`${serviceToDelete?.code} - ${serviceToDelete?.name}`}
      />
    </PageTable>
  );
}
